import { useEffect, useState } from "react";
import {
  Avatar, Box, Card, Chip, Dialog, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useLanguage } from "../../i18n/LanguageContext";
import { visitsApi } from "../../api/resources";
import { COLORS } from "../../theme";

export default function VisitHistoryTable() {
  const { t, lang } = useLanguage();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [provider, setProvider] = useState("");
  const [visits, setVisits] = useState([]);
  const [signatureLightbox, setSignatureLightbox] = useState(null);
  const [photosLightbox, setPhotosLightbox] = useState(null); // arreglo de fotos de una visita, o null

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "es-MX", { day: "2-digit", month: "short" });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(lang === "en" ? "en-US" : "es-MX", { hour: "2-digit", minute: "2-digit" });

  useEffect(() => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (name) params.name = name;
    if (contact) params.contact = contact;
    if (provider) params.provider = provider;
    const id = setTimeout(() => visitsApi.listHistory(params).then(setVisits), 200);
    return () => clearTimeout(id);
  }, [from, to, name, contact, provider]);

  return (
    <Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField size="small" type="date" label={t("areas.filterFrom")} value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t("areas.filterTo")} value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label={t("visitantes.filterName")} value={name} onChange={(e) => setName(e.target.value)} sx={{ width: 150 }} />
        <TextField size="small" label={t("visitantes.filterContact")} value={contact} onChange={(e) => setContact(e.target.value)} sx={{ width: 150 }} />
        <TextField select size="small" label={t("visitantes.filterProvider")} value={provider} onChange={(e) => setProvider(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">{t("common.all")}</MenuItem>
          <MenuItem value="yes">{t("visitantes.onlyProviders")}</MenuItem>
          <MenuItem value="no">{t("visitantes.onlyVisits")}</MenuItem>
        </TextField>
      </Stack>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F4F5EF" }}>
              <TableCell>{t("historial.colDate")}</TableCell>
              <TableCell>{t("visitantes.colVisitor")}</TableCell>
              <TableCell>{t("visitantes.colContactReason")}</TableCell>
              <TableCell>{t("visitantes.colEntry")}</TableCell>
              <TableCell>{t("visitantes.colExit")}</TableCell>
              <TableCell>{t("visitantes.colSignature")}</TableCell>
              <TableCell>{t("common.photos")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {t("visitantes.noVisits")}
                </TableCell>
              </TableRow>
            )}
            {visits.map((v) => (
              <TableRow key={v._id} hover>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{fmtDate(v.entryAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{v.name}</Typography>
                  {v.isProvider && (
                    <Chip
                      size="small"
                      icon={<LocalShippingIcon sx={{ fontSize: 12 }} />}
                      label={t("visitantes.provider")}
                      sx={{ height: 18, fontSize: 10, bgcolor: COLORS.brassSoft, color: "#8A5B18", mt: 0.3 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {v.contactId ? v.contactId.name : "—"}
                    {v.reason && ` · ${v.reason}`}
                  </Typography>
                </TableCell>
                <TableCell>{fmtTime(v.entryAt)}</TableCell>
                <TableCell>
                  {v.exitAt ? fmtTime(v.exitAt) : <Chip size="small" variant="outlined" label={t("visitantes.active")} />}
                </TableCell>
                <TableCell>
                  {v.signature ? (
                    <Avatar
                      variant="rounded"
                      src={v.signature}
                      sx={{ width: 32, height: 24, cursor: "pointer", bgcolor: "#fff", border: `1px solid ${COLORS.line}` }}
                      onClick={() => setSignatureLightbox(v.signature)}
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {v.photos?.length > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: "pointer" }} onClick={() => setPhotosLightbox(v.photos)}>
                      <Avatar variant="rounded" src={v.photos[0]} sx={{ width: 28, height: 28 }} />
                      {v.photos.length > 1 && (
                        <Typography variant="caption" color="text.secondary">+{v.photos.length - 1}</Typography>
                      )}
                    </Stack>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!signatureLightbox} onClose={() => setSignatureLightbox(null)} maxWidth="sm">
        {signatureLightbox && <Box component="img" src={signatureLightbox} sx={{ maxWidth: "100%", maxHeight: "70vh", bgcolor: "#fff", p: 2 }} />}
      </Dialog>

      <Dialog open={!!photosLightbox} onClose={() => setPhotosLightbox(null)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {photosLightbox?.map((src, i) => (
            <Box key={i} component="img" src={src} sx={{ width: 140, height: 140, objectFit: "cover", borderRadius: 1, border: `1px solid ${COLORS.line}` }} />
          ))}
        </Box>
      </Dialog>
    </Box>
  );
}
